'use client'

import { useEffect, useState } from 'react'
import { useBlogDictionary } from '@/providers/BlogDictionaryProvider'
import s from './BlogPostSugestions.module.scss'
import { BasicWrapper } from '@/wrappers/BasicWrapper'
import { ButtonOrLink } from '@/components/ButtonOrLink'
import BlogPostInterface from '@/structures/interfaces/BlogPostInterface'
import { ArchiveInterface } from '@/structures/interfaces/ArchiveInterface'
import { blogPostsToArchive } from '@/utils/archiveAdapters'
import { ArchiveItemPreview } from '@/components/ArchiveItemPreview'
import { DictionaryInterface } from '@/structures/interfaces/DictionaryInterface'

interface BlogAllModeProps {
  mode?: 'all'
  post?: never
}

interface BlogRelatedModeProps {
  mode: 'related'
  post: {
    slug: string
    category: { slug: string }
  }
}

export type BlogProps = BlogAllModeProps | BlogRelatedModeProps

export function BlogPostSugestions({ mode = 'all', post }: BlogProps) {
  const { dict }: { dict: DictionaryInterface | null } = useBlogDictionary()
  const [posts, setPosts] = useState<ArchiveInterface[]>([])

  useEffect(() => {
    const fetchPosts = async () => {
      const res = await fetch('/api/blog/get-posts')
      const data: BlogPostInterface[] = await res.json()

      // Konwertuj BlogPostInterface na ArchiveInterface
      const archivePosts = blogPostsToArchive(data)

      if (mode === 'related' && post) {
        const matching = archivePosts.filter(
          (p) => p.category.slug === post.category.slug && p.slug !== post.slug
        )

        const others = archivePosts.filter(
          (p) => p.category.slug !== post.category.slug && p.slug !== post.slug
        )

        const suggestions = [...matching.slice(0, 3)]

        if (suggestions.length < 3) {
          suggestions.push(...others.slice(0, 3 - suggestions.length))
        }

        setPosts(suggestions)
      } else {
        setPosts(archivePosts.slice(0, 3))
      }
    }

    fetchPosts()
  }, [mode, post])

  if (!dict) {
    return null // lub loading spinner
  }

  return (
    <BasicWrapper
      style={{
        root: {
          paddingTop: '4rem',
          paddingBottom: '5rem',
        },
      }}
    >
      <div className={s.container}>
        <div className={s.header}>
          <h2>
            {mode === 'all'
              ? (dict.homepageTitle as string)
              : (dict.relatedTitle as string)}
          </h2>
          <ButtonOrLink
            as="link"
            href={
              mode === 'all'
                ? ((dict.seeAll as any)?.link as string)
                : `/blog/${post?.category.slug}`
            }
            variant="text"
            classes={s.pulseLink}
            iconRight="Arrow2Icon"
            style={{
              padding: '0',
            }}
          >
            {((dict.seeAll as any)?.title as string) || 'Zobacz wszystkie'}
          </ButtonOrLink>
        </div>
        <div className={s.posts}>
          {posts.map((post) => (
            <ArchiveItemPreview
              key={post.slug}
              data={post}
              dict={dict}
              urlBase="blog"
            />
          ))}
        </div>
      </div>
    </BasicWrapper>
  )
}
